import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import styled from "styled-components";
import { toLocalYYYYMMDD } from "utils/dateFormatters";
import { v4 as uuidv4 } from "uuid";

import { IGroupByListColumn, IGroupByListItem, PerformanceDateRange } from "models";

import CategoryList from "./CategoryList";
import FilterableFieldList from "./FilterableFieldList";
import { useGroupByStore } from "./GroupByStoreProvider";
import SearchBar from "./SearchBar";
import {
  selectCategoryList,
  selectHoveredField,
  selectIsFiltering,
  selectSelectedCategory,
  selectSelectedEndDate,
  selectSelectedStartDate
} from "./groupBySelectors";
import { formatPerformanceDateRange } from "./helpers";
import { useCategories, useCategoryList, useProps } from "./hooks";
import { useFieldPickerKeyboard } from "./hooks/useFieldPickerKeyboard";
import { useSearchBar } from "./hooks/useSearchBar";
import "./toolbar-item-styles.css";

const CUSTOM_FIELDS_CATEGORIES = ["Organization Fields", "My Fields", "Project Fields"];

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    field: IGroupByListColumn,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number,
    performanceDateRange?: PerformanceDateRange
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

function GroupByWrapper({
  dataTypeFilters = [],
  excludedCategoryFilters = [],
  editable = true,
  value = null,
  // To be used in VH value if necessary for the wrapper max-height
  // Potential use case: when the modal has jumped to the top of the screen, decrease the height to fit appropriately
  // height = null,
  onChange,
  ownsFocus = false,
  allowsShapefileFilter = false,
  pinnedPdenSource = null,
  chartType = null,
  axisType = null,
  entityKind,
  chartTypeParam = null,
  isVisible = false,
  hideDateRange = false,
  hideSourceControls = false,
  defaultCategoryName = undefined
}): JSX.Element {
  const selectedCategory = useGroupByStore(selectSelectedCategory);
  const categoryList = useGroupByStore(selectCategoryList);
  const setSelectedCategory = useGroupByStore((s) => s.setSelectedCategory);
  const setFilterText = useGroupByStore((s) => s.setFilterText);
  const setSelectedField = useGroupByStore((s) => s.setSelectedField);
  const disableCustomFieldsEdit = useGroupByStore((s) => s.disableCustomFieldsEdit);
  const selectedStartDate = useGroupByStore(selectSelectedStartDate);
  const selectedEndDate = useGroupByStore(selectSelectedEndDate);

  const searchBar = useSearchBar();
  const categories = useCategories();
  const isFiltering = useGroupByStore(selectIsFiltering);
  const hoveredField = useGroupByStore(selectHoveredField);

  const [, setOrgGeoFieldsCount] = useState<number>(0);

  const instanceId = useMemo(() => `groupby-${uuidv4()}`, []);

  const previousCategoryRef = useRef<Maybe<IGroupByListItem>>(null);

  useCategoryList(entityKind); // syncs categoryList in redux-store with context
  useProps({
    dataTypeFilters,
    excludedCategoryFilters,
    editable,
    value,
    onChange,
    ownsFocus,
    allowsShapefileFilter,
    pinnedPdenSource,
    chartType,
    axisType,
    chartTypeParam,
    defaultCategoryName
  }); // sets initial state

  const handleCategorySelection = useCallback(
    (category: IGroupByListItem) => {
      setSelectedCategory(category);
      setFilterText("");
    },
    [setSelectedCategory, setFilterText]
  );

  const keyboard = useFieldPickerKeyboard({
    zones: ["searchbar", "category", "toolbar", "tabs", "fields"],
    isEnabled: isVisible,
    onCategorySelect: (index) => {
      const category = categoryList?.[index];
      if (category) {
        handleCategorySelection(category);
      }
    },
    onFieldSelect: (index) => {
      const el = keyboard.fieldRefs.current[index];
      el?.click();
    }
  });

  useEffect(() => {
    const wasInFieldsCategory = CUSTOM_FIELDS_CATEGORIES.includes(
      previousCategoryRef.current?.name ?? ""
    );
    const isInFieldsCategory = CUSTOM_FIELDS_CATEGORIES.includes(
      selectedCategory?.name ?? ""
    );

    if (wasInFieldsCategory && !isInFieldsCategory) {
      disableCustomFieldsEdit();
    }
    previousCategoryRef.current = selectedCategory;
  }, [selectedCategory, disableCustomFieldsEdit]);
  const handleFieldSelectionRef = useRef<(fieldData: FieldSelectionData) => void>(null);

  const handleFieldSelection = useCallback(
    (fieldData: FieldSelectionData) => {
      const {
        field,
        onChange,
        isForecastToggleOn,
        selectedForecastFolder,
        selectedForecastFolderName,
        selectedPdenSource
      } = fieldData;
      const performanceDateRange =
        selectedStartDate && selectedEndDate
          ? {
              startDate: toLocalYYYYMMDD(selectedStartDate),
              endDate: toLocalYYYYMMDD(selectedEndDate)
            }
          : undefined;
      // Enrich the field title with the date range for direct consumers (XDA, Info)
      // The main flow re-derives the title via updateGroupBy
      const enrichedField =
        performanceDateRange && field.subgroup === "Date Range"
          ? {
              ...field,
              title: `${field.title} ${formatPerformanceDateRange(
                performanceDateRange.startDate,
                performanceDateRange.endDate
              )}`
            }
          : field;

      if (onChange) {
        onChange(
          enrichedField,
          isForecastToggleOn,
          selectedForecastFolder,
          selectedForecastFolderName,
          selectedPdenSource,
          performanceDateRange
        );
      }

      setSelectedField(field);
      setFilterText("");
    },
    [setSelectedField, setFilterText, selectedStartDate, selectedEndDate]
  );
  handleFieldSelectionRef.current = handleFieldSelection;

  const handleCategoryClick = useCallback(
    (category: IGroupByListItem) => {
      handleCategorySelection(category);
    },
    [handleCategorySelection]
  );

  const handleFieldClick = useCallback(
    (fieldData: FieldSelectionData) => {
      handleFieldSelectionRef.current?.(fieldData);
    },
    [handleFieldSelectionRef]
  );

  const handleOrgGeoFieldsAvailable = useCallback((fieldsCount: number) => {
    setOrgGeoFieldsCount(fieldsCount);
  }, []);

  const { focusedList, focusedIndex } = keyboard;

  return (
    <Wrapper data-groupby-instance={instanceId}>
      <SearchBar
        filterText={searchBar.filterText}
        onFilterChange={searchBar.onFilterChange}
        active={focusedList === "searchbar"}
        isVisible={isVisible}
      />
      <Content>
        <CategoryList
          categoryList={categories}
          selectedCategoryName={selectedCategory?.name ?? null}
          isFiltering={isFiltering}
          hoveredFieldGroup={hoveredField?.group ?? null}
          active={focusedList === "category" && focusedIndex !== null}
          focusedIndex={focusedList === "category" ? focusedIndex : null}
          onItemRef={keyboard.setCategoryRef}
          onCategoryClick={handleCategoryClick}
        />
        <FilterableFieldList
          active={focusedList === "fields" && focusedIndex !== null}
          focusedIndex={focusedList === "fields" ? focusedIndex : null}
          toolbarActive={focusedList === "toolbar" && focusedIndex !== null}
          toolbarFocusedIndex={focusedList === "toolbar" ? focusedIndex : null}
          tabsActive={focusedList === "tabs" && focusedIndex !== null}
          tabsFocusedIndex={focusedList === "tabs" ? focusedIndex : null}
          onFieldItemRef={keyboard.setFieldRef}
          onToolbarSelectRef={keyboard.setToolbarRef}
          onTabItemRef={keyboard.setTabRef}
          onOrgGeoFieldsAvailable={handleOrgGeoFieldsAvailable}
          onFieldClick={handleFieldClick}
          hideDateRange={hideDateRange}
          hideSourceControls={hideSourceControls}
        />
      </Content>
    </Wrapper>
  );
}

export default memo(GroupByWrapper);

const Wrapper = styled.div`
  --category-col-width: max-content;
  --field-col-max-width: 650px;
  --field-col-min-width: 300px;
  --search-width: 180px;
  display: grid;
  grid-template-rows: max-content 1fr;
  gap: 1px;
  min-height: 200px;
  max-height: 800px;
  height: ${(props) => props.offset || 65}vh;
  color: var(--color-text);
  background-color: var(--color-accent);
  border-radius: 4px;
  box-shadow: 0 1px 16px 0 rgba(130, 169, 217, 0.15);
  overflow: hidden;

  .keyboard-focused {
    outline: 2px solid var(--color-primary);
    outline-offset: -2px;
  }
`;

const Content = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: var(--category-col-width) 1fr;
  align-items: stretch;
  justify-items: stretch;
  gap: 1px;
  overflow: hidden;
`;
